import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { AssessmentForm } from './AssessmentForm';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { RoleType, AssessmentStatus, AssessmentType, Decision } from '@promo/shared';
import { AssessmentItem } from './AssessmentsSection';
import { useBarRaiserAssessments } from '@/features/assessments/hooks/useBarRaiserAssessments';
import { useGlobalBarRaisers } from '../hooks/useGlobalBarRaisers';
import { post } from '@/lib/api';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { useQueryClient } from '@tanstack/react-query';
import type { Assessment } from '@promo/shared';
import { Button } from '@/components/ui/button';
import { AssessmentDecisionChart } from '@/components/custom/AssessmentDecisionChart';

interface Props {
  packetId: string;
  readOnly?: boolean;
  isManagerOrSL?: boolean;
  canViewAssessments?: boolean;
  isBarRaiser?: boolean;
}

export function BarRaiserReviewSection({ packetId, readOnly = false, isManagerOrSL = false, canViewAssessments = true, isBarRaiser = false }: Props) {
  const { session } = useAuth();
  const currentUserId = session.user?.id ?? '';
  const isEditable = !readOnly;

  const {
    list: assessments,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
  } = useBarRaiserAssessments(packetId, canViewAssessments);

  const [localPlaceholders, setLocalPlaceholders] = React.useState<Assessment[]>([]);

  // invite dropdown
  const { data: globalBRs = [] } = useGlobalBarRaisers();
  const alreadyAssignedIds = [...assessments, ...localPlaceholders].map(a => a.assessorId);
  const availableOptions: SelectOption[] = globalBRs
    .filter(u => !alreadyAssignedIds.includes(u.id))
    .map(u => ({ value: u.id, label: u.name || u.email }));

  const [selectedUserId, setSelectedUserId] = React.useState('');
  const queryClient = useQueryClient();
  const inviteBarRaiser = async () => {
    if (!selectedUserId) return;
    // assign role on backend
    await post('/roles/assign', { packetId, userId: selectedUserId, roleType: RoleType.BAR_RAISER });
    // optimistic UI: create client-side placeholder assessment
    const now = new Date();
    const placeholder: Assessment = {
      id: `tmp_${selectedUserId}`,
      packetId,
      assessorId: selectedUserId,
      type: AssessmentType.BAR_RAISER,
      status: AssessmentStatus.PENDING,
      decision: undefined,
      justification: undefined,
      createdAt: now,
      updatedAt: now,
    };
    setLocalPlaceholders((prev) => [...prev, placeholder]);
    // refresh server list in background
    queryClient.invalidateQueries({ queryKey: ['assessments', packetId, 'bar-raiser'] });
    setSelectedUserId('');
  };

  const combinedAssessments = React.useMemo(() => {
    // merge server list with local placeholders (avoiding duplicates)
    const ids = new Set<string>();
    const merged: Assessment[] = [];
    [...assessments, ...localPlaceholders].forEach((a) => {
      if (!ids.has(a.assessorId)) {
        merged.push(a);
        ids.add(a.assessorId);
      }
    });
    return merged;
  }, [assessments, localPlaceholders]);

  // Determine visibility: bar raiser sees others only after completing their own
  const currentUserAssessment = combinedAssessments.find(a => a.assessorId === currentUserId);
  const canSeeOthers = isManagerOrSL || !isBarRaiser || (currentUserAssessment?.status === AssessmentStatus.COMPLETED);

  const visibleAssessments = canSeeOthers
    ? combinedAssessments
    : currentUserAssessment
      ? [currentUserAssessment]
      : [];

  const completed = visibleAssessments.filter(a => a.status === AssessmentStatus.COMPLETED).length;

  // Decision chart logic
  const allCompleted = combinedAssessments.length > 0 && combinedAssessments.every(a => a.status === AssessmentStatus.COMPLETED);
  const promoteCount = combinedAssessments.filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.PROMOTE).length;
  const deferCount = combinedAssessments.filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.DEFER).length;

  if (isLoading) {
    return <div className="mb-6"><h2 className="text-xl font-semibold">Bar Raiser Review</h2><div className="flex items-center py-8"><Spinner className="h-8 w-8" /><span className="ml-2">Loading…</span></div></div>;
  }
  if (error) {
    return <div className="mb-6"><h2 className="text-xl font-semibold">Bar Raiser Review</h2><div className="bg-red-50 text-red-800 p-4 rounded-md mt-4">{error.message}</div></div>;
  }

  return (
    <section className="mb-6 space-y-6">
      <div className="flex justify-between lg:justify-start gap-2 items-center mb-6">
        <h2 className="text-xl font-semibold">Bar Raiser Review</h2>
        <Badge>{completed} of {combinedAssessments.length}</Badge>
      </div>

      {/* Decision chart once all Bar Raisers are done */}
      {allCompleted && (
        <div className="my-6 flex justify-center">
          <AssessmentDecisionChart promoteCount={promoteCount} deferCount={deferCount} />
        </div>
      )}

      {isManagerOrSL && availableOptions.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <EditableSelect options={availableOptions} value={selectedUserId} onValueChange={v => setSelectedUserId(v)} placeholder="Select Bar Raiser" className="w-64" />
          <Button disabled={!selectedUserId} onClick={inviteBarRaiser}>Invite</Button>
        </div>
      )}

      {visibleAssessments.length === 0 ? (
        <Skeleton className="h-4 w-3/4" />
      ) : (
        visibleAssessments.map(item => (
          <AssessmentItem
            key={item.id}
            item={item}
            currentUserId={currentUserId}
            isEditable={isEditable && item.assessorId === currentUserId}
            isManager={isManagerOrSL}
            updateAssessment={updateAssessment}
            deleteAssessment={deleteAssessment}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        ))
      )}

      {isEditable && isBarRaiser && !combinedAssessments.some(a => a.assessorId === currentUserId) && (
        <div className="py-6 space-y-4 border-b last:border-b-0">
          <AssessmentForm
            onSubmit={data => createAssessment({ ...data, packetId, type: AssessmentType.BAR_RAISER })}
            onCancel={() => {}}
            isSubmitting={isCreating}
          />
        </div>
      )}
    </section>
  );
} 